import React from "react";
import { useSearchParams } from "react-router-dom";
import PageHeader from "src/components/pageHeader";
import Pagination from "src/components/pagination";
import usePagination from "src/customHooks/usePagination";
import { useTheme } from "src/context/theme.context";
import { RecruiterTable } from "../components/RecruiterTable";
import { useCountRecruiterRowsQuery, useGetRecruiterRowsQuery } from "../recruiter.service";
import { RecruiterKind, RecruiterRow } from "../types/recruiter.entity";

const TABS: { kind: RecruiterKind; label: string }[] = [
  { kind: "applications", label: "Applications" },
  { kind: "help-requests", label: "Help requests" },
  { kind: "searches", label: "Saved searches" },
];

const TAB_PARAM = "tab";

const parseKind = (value: string | null): RecruiterKind =>
  TABS.some((tab) => tab.kind === value) ? (value as RecruiterKind) : "applications";

/**
 * One tab's list. Mounted per tab (keyed on the kind) so each gets its own
 * pagination: the tab is a URL param, and switching it drops the page params,
 * which is what resets `usePagination` to page one.
 */
const RecruiterList = ({ kind }: { kind: RecruiterKind }) => {
  const { isDarkMode } = useTheme();
  const { data, numberOfPages, currentPage, next, prev, isSuccess, isError, isLoading, isFetching, error } =
    usePagination<RecruiterRow>({
      useGetDataQuery: useGetRecruiterRowsQuery,
      useCountData: useCountRecruiterRowsQuery,
      initQuery: { kind },
    });

  if (isError) {
    return (
      <div
        className={`rounded-2xl border p-8 text-center ${
          isDarkMode ? "border-red-800 bg-red-950/40 text-red-200" : "border-red-200 bg-red-50 text-red-800"
        }`}
      >
        <p className="font-semibold">Could not load these records.</p>
        <p className="mt-1 text-sm">{typeof error === "string" ? error : "Try again in a moment."}</p>
      </div>
    );
  }

  // The hook's result is a union on `isSuccess`; only the success side carries
  // a page count, so the list waits for it as the function-errors page does.
  if (!isSuccess) return <RecruiterTable kind={kind} data={[]} isLoading />;

  return (
    <>
      <RecruiterTable kind={kind} data={data} isLoading={isLoading || !!isFetching} />
      <Pagination
        numberOfPages={numberOfPages}
        currentPage={currentPage}
        prev={prev}
        next={next}
        isLoading={isLoading || isFetching}
      />
    </>
  );
};

const TabCount = ({ kind }: { kind: RecruiterKind }) => {
  const { data, isLoading, isError } = useCountRecruiterRowsQuery({ where: { kind } });
  if (isLoading || isError || typeof data !== "number") return null;
  return <span className="ml-2 rounded-full bg-black/10 px-2 py-0.5 text-xs dark:bg-white/10">{data}</span>;
};

const RecruiterPage = () => {
  const { isDarkMode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const kind = parseKind(searchParams.get(TAB_PARAM));

  const cardBg = isDarkMode
    ? "bg-gradient-to-br from-gray-900/80 to-gray-800/80 border border-gray-700"
    : "bg-gradient-to-br from-white to-gray-50 border border-gray-200 shadow-lg";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-600";

  return (
    <div className="page_container">
      <PageHeader text="Recruiter Inbox" />

      <div className={`rounded-2xl ${cardBg} p-6 mb-6`}>
        <p className={`text-sm ${muted}`}>
          What learners sent the Taaly recruiter in the app. Each learner was told that a person at Taaly will look at
          it, and nothing has been sent to the employer. Records and CVs are deleted 180 days after the learner's last
          activity.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap gap-2" role="tablist">
        {TABS.map((tab) => {
          const active = tab.kind === kind;
          return (
            <button
              key={tab.kind}
              role="tab"
              aria-selected={active}
              onClick={() => setSearchParams({ [TAB_PARAM]: tab.kind })}
              className={`rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                active
                  ? "bg-emerald-600 text-white shadow"
                  : isDarkMode
                    ? "bg-gray-800 text-gray-300 hover:bg-gray-700"
                    : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
              }`}
            >
              {tab.label}
              <TabCount kind={tab.kind} />
            </button>
          );
        })}
      </div>

      <RecruiterList key={kind} kind={kind} />
    </div>
  );
};

export default RecruiterPage;
